// Utilidades para manejo correcto de fechas sin problemas de zona horaria

/**
 * Parsea una fecha desde string sin conversión de zona horaria
 * @param {string} dateString - Fecha en formato YYYY-MM-DD o YYYY-MM-DD HH:MM:SS
 * @returns {Date} - Objeto Date en zona horaria local
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  
  // Si la fecha ya es un objeto Date, devolverla tal como está
  if (dateString instanceof Date) return dateString;
  
  // Convertir a string si no lo es
  const dateStr = String(dateString).trim();
  
  let parsedDate = null;

  // CASO ESPECIAL: Fechas de base de datos MySQL (YYYY-MM-DD HH:MM:SS)
  // Ejemplo: "2025-06-30 00:00:00" debe ser 30 de junio, no 29
  if (dateStr.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
    const [datePart, timePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);
    parsedDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  }
  // CASO ESPECIAL: Solo fecha de base de datos (YYYY-MM-DD)
  // Ejemplo: "2025-06-30" debe ser 30 de junio
  // CORRECCIÓN: Usar fecha local en lugar de UTC para evitar conversiones de zona horaria
  else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    parsedDate = new Date(year, month - 1, day, 12, 0, 0); // Use local time, not UTC
  }
  // Si la fecha contiene 'T' o 'Z', es ISO string y puede causar problemas
  else if (dateStr.includes('T') || dateStr.includes('Z')) {
    // SOLUCIÓN CORREGIDA: Si termina en 'Z', es UTC y debe extraerse manualmente para evitar conversión automática
    if (dateStr.endsWith('Z')) {
      // Es una fecha UTC, extraer componentes manualmente para evitar conversión automática
      const cleanDateStr = dateStr.replace('T', ' ').replace('Z', '').split('.')[0];
      const [datePart, timePart] = cleanDateStr.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      
      if (timePart && timePart.includes(':')) {
        const [hour, minute, second] = timePart.split(':').map(Number);
        parsedDate = new Date(year, month - 1, day, hour || 0, minute || 0, second || 0);
      } else {
        parsedDate = new Date(year, month - 1, day, 12, 0, 0);
      }
    }
    // Para fechas ISO sin 'Z' (hora local), extraer manualmente
    else {
      const cleanDateStr = dateStr.split('T')[0];
      const timePart = dateStr.includes('T') ? dateStr.split('T')[1].split('.')[0] : '';
      
      const [year, month, day] = cleanDateStr.split('-').map(Number);
      
      if (timePart && timePart.includes(':')) {
        const [hour, minute, second] = timePart.split(':').map(Number);
        parsedDate = new Date(year, month - 1, day, hour || 0, minute || 0, second || 0);
      } else {
        parsedDate = new Date(year, month - 1, day, 12, 0, 0);
      }
    }
  }
  // Manejar otros formatos de fecha comunes
  else if (dateStr.includes('-')) {
    const parts = dateStr.split(' ');
    const datePart = parts[0];
    const timePart = parts[1];
    
    const [year, month, day] = datePart.split('-').map(Number);
    
    if (timePart && timePart.includes(':')) {
      const [hour, minute, second] = timePart.split(':').map(Number);
      parsedDate = new Date(year, month - 1, day, hour || 0, minute || 0, second || 0);
    } else {
      parsedDate = new Date(year, month - 1, day, 12, 0, 0);
    }
  }
  // Último recurso: si parece ser una fecha YYYY-MM-DD, parsear manualmente
  else if (dateStr.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    parsedDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)); // Use noon UTC
  }
  // Como último recurso, usar Date constructor pero con advertencia
  else {
    console.warn('⚠️ parseLocalDate - Usando Date constructor como último recurso:', dateString);
    parsedDate = new Date(dateString);
  }

  if (parsedDate && !isNaN(parsedDate.getTime())) {
    console.log('📅 parseLocalDate - Parsed date:', dateString, '->', parsedDate);
    return parsedDate;
  }
  
  console.warn('⚠️ parseLocalDate - Fecha inválida o no reconocida:', dateString);
  return null;
};

/**
 * Formatea una fecha para mostrar solo la fecha (sin hora)
 * @param {string|Date} dateString - Fecha a formatear
 * @returns {string} - Fecha formateada
 */
export const formatDateOnly = (dateString) => {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) {
    console.warn('⚠️ formatDateOnly - Fecha inválida:', dateString);
    return 'Sin fecha';
  }
  
  // Extract local date components
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Create a new Date object using local components to ensure correct local date formatting
  const localDateForFormatting = new Date(year, month, day);

  const formatted = localDateForFormatting.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  
  console.log('📅 formatDateOnly - Input:', dateString, '-> Output:', formatted);
  return formatted;
};

/**
 * Función específica para formatear fechas en la minuta ejecutiva
 * Usa parseLocalDate corregido que ya maneja correctamente las fechas sin conversión de zona horaria
 * @param {string|Date} dateString - Fecha a formatear
 * @returns {string} - Fecha formateada correctamente
 */
export const formatDateForExecutiveSummary = (dateString) => {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) {
    console.warn('⚠️ formatDateForExecutiveSummary - Fecha inválida:', dateString);
    return 'Sin fecha';
  }
  
  // Ya no necesitamos agregar un día porque parseLocalDate ahora maneja correctamente las fechas
  const formatted = date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  console.log('📅 formatDateForExecutiveSummary - Input:', dateString, '-> Output:', formatted);
  return formatted;
};

/**
 * Función específica para obtener el día de la semana en la minuta ejecutiva
 * Usa parseLocalDate corregido que ya maneja correctamente las fechas sin conversión de zona horaria
 * @param {string|Date} dateString - Fecha
 * @returns {string} - Día de la semana correcto
 */
export const getWeekdayForExecutiveSummary = (dateString) => {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return '';
  
  // Ya no necesitamos agregar un día porque parseLocalDate ahora maneja correctamente las fechas
  return date.toLocaleDateString('es-MX', { weekday: 'long' });
};

/**
 * Formatea una fecha para mostrar fecha y hora
 * @param {string|Date} dateString - Fecha a formatear
 * @returns {string} - Fecha y hora formateadas
 */
export const formatDateTime = (dateString) => {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return 'Sin fecha';
  
  const dateStr = date.toLocaleDateString('es-MX');
  const timeStr = date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  
  return `${dateStr}, ${timeStr}`;
};

/**
 * Formatea una fecha para input de tipo date (YYYY-MM-DD)
 * @param {string|Date} dateString - Fecha a formatear
 * @returns {string} - Fecha en formato YYYY-MM-DD
 */
export const formatForInput = (dateString) => {
  // SOLUCIÓN CRÍTICA: Si es un objeto Date nativo (como new Date()), 
  // usar directamente sus métodos sin parseLocalDate para evitar conversiones
  if (dateString instanceof Date) {
    if (isNaN(dateString.getTime())) return '';
    
    const year = dateString.getFullYear();
    const month = String(dateString.getMonth() + 1).padStart(2, '0');
    const day = String(dateString.getDate()).padStart(2, '0');
    
    console.log('📅 formatForInput - Date nativo:', dateString, '-> Output:', `${year}-${month}-${day}`);
    return `${year}-${month}-${day}`;
  }
  
  // Para strings, usar parseLocalDate
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  console.log('📅 formatForInput - String parseado:', dateString, '-> Output:', `${year}-${month}-${day}`);
  return `${year}-${month}-${day}`;
};

/**
 * Obtiene el día de la semana en español
 * @param {string|Date} dateString - Fecha
 * @returns {string} - Día de la semana
 */
export const getWeekday = (dateString) => {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('es-MX', { weekday: 'long' });
};

/**
 * Verifica si una fecha es hoy
 * @param {string|Date} dateString - Fecha a verificar
 * @returns {boolean} - True si es hoy
 */
export const isToday = (dateString) => {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return false;
  
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

/**
 * Calcula días entre dos fechas
 * @param {string|Date} date1 - Primera fecha
 * @param {string|Date} date2 - Segunda fecha
 * @returns {number} - Diferencia en días
 */
export const daysBetween = (date1, date2) => {
  const d1 = parseLocalDate(date1);
  const d2 = parseLocalDate(date2);
  
  if (!d1 || !d2 || isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
  
  const diffTime = Math.abs(d2 - d1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Formatea una fecha con opciones personalizadas
 * @param {string|Date} dateString - Fecha a formatear
 * @param {object} options - Opciones de formato
 * @returns {string} - Fecha formateada
 */
export const formatLocalDate = (dateString, options = {}) => {
  const date = parseLocalDate(dateString);
  if (!date || isNaN(date.getTime())) return 'Fecha inválida';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };
  
  return date.toLocaleDateString('es-MX', defaultOptions);
};
